import { useState, useEffect, useRef } from 'react'
import {
  getPacotes,
  createPacote,
  updatePacote,
  deletePacote,
  getServicos,
  uploadImage,
  type Pacote,
  type Servico,
} from '@/api/adminApi'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

const FORM_EMPTY = {
  nome: '',
  descricao: '',
  preco: '' as string | number,
  imagem_url: '' as string | null,
  servicos_ids: [] as number[],
  ativo: true,
  ordem: 0,
}

export function AdminPacotesPage() {
  const fileRef = useRef<HTMLInputElement>(null)
  const [list, setList] = useState<Pacote[]>([])
  const [servicos, setServicos] = useState<Servico[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Pacote | null>(null)
  const [form, setForm] = useState(FORM_EMPTY)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const { notifySaved, notifyDeleted, notifyError, notifyValidation } = useSaveFeedback()

  const load = () => {
    setError(null)
    Promise.all([getPacotes(true), getServicos(false)])
      .then(([pacotes, svc]) => {
        setList(pacotes)
        setServicos(svc)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const resetForm = () => {
    setEditing(null)
    setForm(FORM_EMPTY)
  }

  const toggleServico = (id: number) => {
    setForm((f) => ({
      ...f,
      servicos_ids: f.servicos_ids.includes(id)
        ? f.servicos_ids.filter((x) => x !== id)
        : [...f.servicos_ids, id],
    }))
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const { url } = await uploadImage(file)
      setForm((f) => ({ ...f, imagem_url: url }))
      notifySaved('Imagem carregada.')
    } catch (err) {
      notifyError(err, 'Erro no upload')
      setError(err instanceof Error ? err.message : 'Erro no upload')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nome.trim()) {
      notifyValidation('Indique o nome do pacote.')
      return
    }
    setSaving(true)
    setError(null)
    const preco = form.preco === '' ? undefined : Number(form.preco)
    const body = {
      nome: form.nome.trim(),
      descricao: form.descricao.trim() || undefined,
      preco,
      imagem_url: form.imagem_url || null,
      servicos_ids: form.servicos_ids,
      ativo: form.ativo,
      ordem: form.ordem,
    }
    try {
      if (editing) {
        await updatePacote(editing.id, body)
        notifySaved('Pacote actualizado.')
      } else {
        await createPacote(body)
        notifySaved('Pacote criado.')
      }
      resetForm()
      load()
    } catch (e) {
      notifyError(e, 'Erro ao salvar')
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (p: Pacote) => {
    setEditing(p)
    setForm({
      nome: p.nome,
      descricao: p.descricao ?? '',
      preco: p.preco ?? '',
      imagem_url: p.imagem_url ?? null,
      servicos_ids: p.servicos_ids ?? [],
      ativo: !!p.ativo,
      ordem: p.ordem ?? 0,
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Excluir este pacote?')) return
    setError(null)
    try {
      await deletePacote(id)
      if (editing?.id === id) resetForm()
      load()
      notifyDeleted('Pacote removido.')
    } catch (e) {
      notifyError(e, 'Erro ao excluir')
      setError(e instanceof Error ? e.message : 'Erro ao excluir')
    }
  }

  if (loading) return <p className={styles.placeholderMsg}>A carregar…</p>

  return (
    <>
      <header className={styles.sectionHeader}>
        <h1 className={styles.sectionTitle}>Pacotes</h1>
        <p className={styles.sectionSubtitle}>
          Crie e edite os pacotes de serviços exibidos no app. Associe serviços do catálogo e uma imagem
          de capa.
        </p>
      </header>
      {error && (
        <div className={styles.erroSection}>
          <p className={styles.erro}>{error}</p>
        </div>
      )}
      <div className={styles.sectionCard}>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label>Nome *</label>
            <input
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Nome do pacote"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label>Descrição</label>
            <textarea
              value={form.descricao}
              onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
              placeholder="Descrição"
              rows={3}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Imagem</label>
            <div className={styles.uploadStack}>
              <div className={styles.uploadRow}>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleUpload}
                  disabled={uploading}
                  className={styles.uploadInputHidden}
                  aria-label="Carregar imagem do pacote"
                />
                <button
                  type="button"
                  className={styles.btnUpload}
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'A enviar…' : form.imagem_url ? 'Trocar imagem' : 'Carregar imagem'}
                </button>
                {form.imagem_url && (
                  <button
                    type="button"
                    className={styles.btnCancel}
                    onClick={() => setForm((f) => ({ ...f, imagem_url: null }))}
                    disabled={uploading}
                  >
                    Remover
                  </button>
                )}
              </div>
              {form.imagem_url ? (
                <img
                  src={resolveMediaUrl(form.imagem_url)}
                  alt="Pré-visualização"
                  className={styles.pacoteImagemPreview}
                />
              ) : (
                <p className={styles.uploadEmptyHint}>Nenhuma imagem seleccionada.</p>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Serviços incluídos</label>
            {servicos.length === 0 ? (
              <p className={styles.pageHint}>Nenhum serviço no catálogo.</p>
            ) : (
              <div className={styles.servicosMultiSelect}>
                {servicos.map((s) => (
                  <label key={s.id} className={styles.checkboxInline}>
                    <input
                      type="checkbox"
                      checked={form.servicos_ids.includes(s.id)}
                      onChange={() => toggleServico(s.id)}
                    />
                    {s.nome}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className={styles.formGroup}>
            <label>Preço</label>
            <input
              type="number"
              step="0.01"
              value={form.preco}
              onChange={(e) => setForm((f) => ({ ...f, preco: e.target.value }))}
              placeholder="0.00"
            />
          </div>
          <div className={`${styles.formGroup} ${styles.formRow}`}>
            <input
              type="checkbox"
              id="pacote-ativo"
              checked={form.ativo}
              onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
            />
            <label htmlFor="pacote-ativo">Ativo</label>
          </div>
          <div className={styles.formGroup}>
            <label>Ordem</label>
            <input
              type="number"
              value={form.ordem}
              onChange={(e) => setForm((f) => ({ ...f, ordem: Number(e.target.value) || 0 }))}
            />
          </div>
          <div className={styles.formActions}>
            <button type="submit" className={styles.btnSalvar} disabled={saving}>
              {saving ? 'A guardar…' : editing ? 'Atualizar' : 'Adicionar'}
            </button>
            {editing && (
              <button type="button" className={styles.btnCancel} onClick={resetForm}>
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>
      <div className={styles.sectionListCard}>
        <h2 className={styles.sectionListTitle}>Lista de pacotes</h2>
        {list.length === 0 ? (
          <p className={styles.listEmpty}>Nenhum pacote. Adicione um acima.</p>
        ) : (
          <div className={styles.listScrollArea}>
            <ul className={styles.list}>
            {list.map((p) => (
              <li key={p.id} className={styles.listItem}>
                <div className={styles.listItemContent}>
                  <div className={styles.listItemTitle}>
                    {p.nome}
                    {p.preco != null && (
                      <span style={{ color: 'var(--accent)', marginLeft: 8, fontWeight: 600 }}>
                        € {Number(p.preco).toFixed(2)}
                      </span>
                    )}
                  </div>
                  {p.descricao && <div className={styles.listItemMeta}>{p.descricao}</div>}
                  {p.servicos_ids && p.servicos_ids.length > 0 && (
                    <div className={styles.listItemMeta}>
                      {p.servicos_ids.length} serviço(s) associado(s)
                    </div>
                  )}
                  {!p.ativo && <span className={styles.listItemMeta}>(inativo)</span>}
                </div>
                <div className={styles.listItemActions}>
                  <button type="button" onClick={() => handleEdit(p)} className={styles.btnSecondary}>
                    Editar
                  </button>
                  <button type="button" onClick={() => handleDelete(p.id)} className={styles.btnDanger}>
                    Excluir
                  </button>
                </div>
              </li>
            ))}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}
